import { isMobile } from '@layerswap/utils';
import { walletKey } from '../walletKey.js';
import { getDynamicWcMetadata, getPendingDynamicWcMetadata } from './dynamicMetadata.js';
import { isSafeDeepLink } from './foregroundWalletApp.js';
import { recordWalletRequest } from './walletRequestStore.js';
function resolveDeepLink(session, namespace) {
    const peer = session.peer.metadata;
    // The session identifies the wallet that will actually receive the request.
    // Address-only metadata can belong to another wallet using the same account.
    const address = session.namespaces[namespace]?.accounts[0]?.split(':').slice(2).join(':');
    const metadata = [
        getPendingDynamicWcMetadata(namespace),
        address ? getDynamicWcMetadata(namespace, address) : null,
    ].find(meta => meta?.deepLink && walletKey(meta.name) === walletKey(peer.name));
    const link = [peer.redirect?.native, peer.redirect?.universal, metadata?.deepLink]
        .find(candidate => candidate && isSafeDeepLink(candidate));
    if (!link)
        return undefined;
    // Use the same MetaMask workaround as the pairing link builder.
    return walletKey(peer.name) === 'metamask' ? 'https://metamask.app.link' : link;
}
export function buildWalletRequestLink(link, id, topic) {
    const url = new URL(link);
    if (url.protocol === 'https:' && url.hostname === 't.me') {
        const payload = btoa(`requestId=${id}&sessionTopic=${topic}`)
            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        url.searchParams.set('startapp', payload);
        return url.toString();
    }
    // Registry entries can already include /wc (e.g. okex://main/wc).
    if (url.hostname !== 'wc' && !url.pathname.replace(/\/$/, '').endsWith('/wc')) {
        if (link.endsWith('://'))
            url.hostname = 'wc';
        else
            url.pathname = `${url.pathname.replace(/\/$/, '')}/wc`;
    }
    url.searchParams.delete('uri');
    url.searchParams.set('requestId', String(id));
    url.searchParams.set('sessionTopic', topic);
    return url.toString();
}
/**
 * Our registry connectors own their redirects. WalletConnect's default uses
 * the origin-wide WALLETCONNECT_DEEPLINK_CHOICE, which may name a different
 * EVM/Solana wallet. Disable that path only for these sessions and open the
 * session's wallet after the signing request has reached the relay.
 */
export function subscribeWalletRequests(client, namespace) {
    const configureSession = (session) => {
        if (!isMobile() || !session.namespaces[namespace] || session.sessionConfig?.disableDeepLink)
            return;
        // Store.update replaces the in-memory session synchronously, before its
        // persistence promise settles and before connect() can return to callers.
        void client.session.update(session.topic, {
            sessionConfig: { ...session.sessionConfig, disableDeepLink: true },
        }).catch(error => console.warn('Could not persist WalletConnect redirect settings', error));
    };
    const onConnect = ({ session }) => configureSession(session);
    const onRequest = ({ topic, id, chainId, request }) => {
        if (!isMobile() || chainId.split(':')[0] !== namespace)
            return;
        // Capability discovery and receipt polling must not background the browser.
        if (request?.method === 'wallet_getCapabilities'
            || request?.method === 'wallet_getCallsStatus') {
            return;
        }
        try {
            const session = client.session.get(topic);
            // Link-mode sessions carry their request in the SDK's own app link.
            if (session.transportType === 'link_mode')
                return;
            const link = resolveDeepLink(session, namespace);
            const href = link ? buildWalletRequestLink(link, id, topic) : undefined;
            recordWalletRequest({
                walletName: session.peer.metadata.name,
                topic,
                namespace,
                accounts: session.namespaces[namespace]?.accounts.map(account => account.split(':').slice(2).join(':')) ?? [],
                href,
            });
            if (href)
                window.location.href = href;
            else
                console.warn('WalletConnect request sent without a wallet link', { wallet: session.peer.metadata.name, namespace });
        }
        catch (error) {
            // A deleted session or blocked navigation must not reject a request
            // that has already been published and can still be approved manually.
            console.warn('Could not open the WalletConnect wallet', error);
        }
    };
    client.on('session_connect', onConnect);
    client.on('session_request_sent', onRequest);
    client.session.getAll().forEach(configureSession);
    return () => {
        client.off('session_connect', onConnect);
        client.off('session_request_sent', onRequest);
    };
}
